import { Navigate, Outlet, useLocation } from "react-router-dom";
import { STAFF_APP_ROLES, useUserRoles } from "@/hooks/useUserRoles";
import { useAccessContext } from "@/hooks/useAccessContext";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { useOrganization } from "@/hooks/useOrganization";
import { AccessLoadError } from "./AccessLoadError";

export function StaffAccessGate({ allowVolunteers = false }: { allowVolunteers?: boolean } = {}) {
  const location = useLocation();
  const { isStaff, hasAnyRole, hasAnyOrgRole, isLoading: rolesLoading } = useUserRoles();
  const { data: accessContext, isLoading: accessLoading, isFetching: accessFetching, rolesUnavailable, refetch: retryAccess } = useAccessContext();
  const { isOrgAdmin, isLoading: orgLoading } = useOrganization();

  const isStaffMember = Boolean(
    accessContext?.is_staff ||
      accessContext?.capabilities?.isOrgAdmin ||
      isOrgAdmin ||
      isStaff ||
      hasAnyRole(STAFF_APP_ROLES),
  );

  // Some shared stations (event/kids check-in) are operable by a plain "volunteer"
  // who is not staff. Opt-in per route via allowVolunteers. Uses hasAnyOrgRole
  // (volunteer role scoped to the ACTIVE org or a global grant) — NOT the all-orgs
  // hasAnyRole — so a volunteer in org A isn't admitted to org B's station UI only
  // to hit 42501 from the org-scoped server RPCs (Codex #2843).
  const isVolunteerOperator = allowVolunteers && hasAnyOrgRole(["volunteer"]);

  // Wait while the access context is loading.
  if (rolesLoading || accessLoading || orgLoading) {
    return <ForgePageLoader />;
  }

  if (rolesUnavailable) {
    return <AccessLoadError onRetry={() => { void retryAccess(); }} />;
  }

  if (isStaffMember || isVolunteerOperator) {
    return <Outlet />;
  }

  // Not-staff but still fetching fresh data: wait for the settled result.
  if (accessFetching) {
    return <ForgePageLoader />;
  }

  return <Navigate to="/member" replace state={{ from: location }} />;
}
